import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../services/api";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const safeMsg = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const toDateInput = (v) => {
  if (!v) return "";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : "";
};

const emptyExperience = () => ({
  company: "",
  position: "",
  location: "",
  start_date: "",
  end_date: "",
  description: "",
});

const emptyEducation = () => ({
  institution: "",
  degree: "",
  field: "",
  graduation_date: "",
});

const emptyLanguage = () => ({
  language: "",
  proficiency: "",
});

const emptyCertificate = () => ({
  title: "",
  issuer: "",
  issue_date: "",
  expiry_date: "",
  file_path: "",
});

export default function PublicEmployeeCvPage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [link, setLink] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({
    full_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    address: "",
    nationality: "",
    current_job_title: "",
    years_experience: "",
    relocate: false,
    expected_salary: "",
    current_salary: "",
    joining_availability_date: "",
    about_me: "",
    skills: "",
    notes: "",
    submitted_at: null,
  });

  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [certificates, setCertificates] = useState([]);

  const locked = useMemo(() => Boolean(profile?.submitted_at), [profile?.submitted_at]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    setInfo("");

    try {
      const res = await api.get(`/public/employee-cv/${token}`);
      const out = res?.data?.data ?? {};

      setLink(out?.link ?? null);
      setUser(out?.user ?? null);

      const p = out?.profile ?? {};
      setProfile({
        full_name: p.full_name ?? "",
        email: p.email ?? "",
        phone: p.phone ?? "",
        date_of_birth: toDateInput(p.date_of_birth),
        address: p.address ?? "",
        nationality: p.nationality ?? "",
        current_job_title: p.current_job_title ?? "",
        years_experience: p.years_experience ?? "",
        relocate: !!p.relocate,
        expected_salary: p.expected_salary ?? "",
        current_salary: p.current_salary ?? "",
        joining_availability_date: toDateInput(p.joining_availability_date),
        about_me: p.about_me ?? p.summary ?? "",
        skills: p.skills ?? "",
        notes: p.notes ?? "",
        submitted_at: p.submitted_at ?? null,
      });

      setExperiences(
        (out?.experiences ?? []).map((x) => ({
          id: x.id,
          company: x.company ?? "",
          position: x.position ?? "",
          location: x.location ?? "",
          start_date: toDateInput(x.start_date),
          end_date: toDateInput(x.end_date),
          description: x.description ?? "",
          sort_order: x.sort_order ?? undefined,
        }))
      );

      setEducations(
        (out?.educations ?? []).map((x) => ({
          id: x.id,
          institution: x.institution ?? "",
          degree: x.degree ?? "",
          field: x.field ?? "",
          graduation_date: toDateInput(x.graduation_date),
          sort_order: x.sort_order ?? undefined,
        }))
      );

      setLanguages(
        (out?.languages ?? []).map((x) => ({
          id: x.id,
          language: x.language ?? "",
          proficiency: x.proficiency ?? "",
          sort_order: x.sort_order ?? undefined,
        }))
      );

      setCertificates(
        (out?.certificates ?? []).map((x) => ({
          id: x.id,
          title: x.title ?? "",
          issuer: x.issuer ?? "",
          issue_date: toDateInput(x.issue_date),
          expiry_date: toDateInput(x.expiry_date),
          file_path: x.file_path ?? "",
          sort_order: x.sort_order ?? undefined,
        }))
      );
    } catch (e) {
      setError(safeMsg(e, "Unable to load this CV link."));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const updateArrayRow = (setRows, idx, key, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };

  const removeArrayRow = (setRows, idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveAll = async () => {
    if (locked) return;
    setSaving(true);
    setError("");
    setInfo("");

    try {
      const payload = {
        ...profile,
        experiences,
        educations,
        languages,
        certificates,
      };

      const res = await api.put(`/public/employee-cv/${token}`, payload);
      const out = res?.data?.data ?? {};

      setInfo("Saved");
      setLink(out?.link ?? link);
      setUser(out?.user ?? user);

      const p = out?.profile ?? {};
      setProfile((prev) => ({
        ...prev,
        ...p,
        date_of_birth: toDateInput(p.date_of_birth ?? prev.date_of_birth),
        joining_availability_date: toDateInput(p.joining_availability_date ?? prev.joining_availability_date),
      }));

      setExperiences(
        (out?.experiences ?? []).map((x) => ({
          id: x.id,
          company: x.company ?? "",
          position: x.position ?? "",
          location: x.location ?? "",
          start_date: toDateInput(x.start_date),
          end_date: toDateInput(x.end_date),
          description: x.description ?? "",
          sort_order: x.sort_order ?? undefined,
        }))
      );

      setEducations(
        (out?.educations ?? []).map((x) => ({
          id: x.id,
          institution: x.institution ?? "",
          degree: x.degree ?? "",
          field: x.field ?? "",
          graduation_date: toDateInput(x.graduation_date),
          sort_order: x.sort_order ?? undefined,
        }))
      );

      setLanguages(
        (out?.languages ?? []).map((x) => ({
          id: x.id,
          language: x.language ?? "",
          proficiency: x.proficiency ?? "",
          sort_order: x.sort_order ?? undefined,
        }))
      );

      setCertificates(
        (out?.certificates ?? []).map((x) => ({
          id: x.id,
          title: x.title ?? "",
          issuer: x.issuer ?? "",
          issue_date: toDateInput(x.issue_date),
          expiry_date: toDateInput(x.expiry_date),
          file_path: x.file_path ?? "",
          sort_order: x.sort_order ?? undefined,
        }))
      );
    } catch (e) {
      setError(safeMsg(e, "Failed to save CV."));
    } finally {
      setSaving(false);
    }
  };

  const submitAll = async () => {
    if (locked) return;
    setSubmitting(true);
    setError("");
    setInfo("");

    try {
      await api.post(`/public/employee-cv/${token}/submit`);
      setInfo("Submitted");
      await load();
    } catch (e) {
      setError(safeMsg(e, "Failed to submit CV."));
    } finally {
      setSubmitting(false);
    }
  };

  const generatePdf = async () => {
    setGenerating(true);
    setError("");
    setInfo("");

    try {
      const res = await api.get(`/public/employee-cv/${token}/generate`, {
        params: { download: 1 },
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(safeMsg(e, "Failed to generate CV PDF."));
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={900}>
            Employee CV
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.name ? `${user.name}${user?.employee_code ? ` (${user.employee_code})` : ""}` : "Public link"}
          </Typography>
        </Box>

        {link ? (
          <Card>
            <CardContent>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    size="small"
                    label={(link?.status || "active").toUpperCase()}
                    color={link?.status === "active" ? "success" : link?.status === "expired" ? "warning" : "default"}
                    variant="outlined"
                  />
                  {locked ? <Chip size="small" label="LOCKED" color="warning" variant="outlined" /> : null}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Expires: {link?.expires_at ? new Date(link.expires_at).toLocaleString() : "—"} · Last used:{" "}
                  {link?.last_used_at ? new Date(link.last_used_at).toLocaleString() : "—"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}
        {info ? <Alert severity="success">{info}</Alert> : null}

        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
              Profile
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Full name"
                  value={profile.full_name}
                  onChange={(e) => setProfile((p) => ({ ...p, full_name: e.target.value }))}
                  fullWidth
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  value={profile.email}
                  onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                  fullWidth
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  value={profile.phone}
                  onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                  fullWidth
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Date of birth"
                  type="date"
                  value={profile.date_of_birth}
                  onChange={(e) => setProfile((p) => ({ ...p, date_of_birth: e.target.value }))}
                  fullWidth
                  disabled={locked}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nationality"
                  value={profile.nationality}
                  onChange={(e) => setProfile((p) => ({ ...p, nationality: e.target.value }))}
                  fullWidth
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  value={profile.address}
                  onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))}
                  fullWidth
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Current job title"
                  value={profile.current_job_title}
                  onChange={(e) => setProfile((p) => ({ ...p, current_job_title: e.target.value }))}
                  fullWidth
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Years of experience"
                  value={profile.years_experience}
                  onChange={(e) => setProfile((p) => ({ ...p, years_experience: e.target.value }))}
                  fullWidth
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Expected salary"
                  value={profile.expected_salary}
                  onChange={(e) => setProfile((p) => ({ ...p, expected_salary: e.target.value }))}
                  fullWidth
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Current salary"
                  value={profile.current_salary}
                  onChange={(e) => setProfile((p) => ({ ...p, current_salary: e.target.value }))}
                  fullWidth
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Availability date"
                  type="date"
                  value={profile.joining_availability_date}
                  onChange={(e) => setProfile((p) => ({ ...p, joining_availability_date: e.target.value }))}
                  fullWidth
                  disabled={locked}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="About me"
                  value={profile.about_me}
                  onChange={(e) => setProfile((p) => ({ ...p, about_me: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={3}
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Skills"
                  value={profile.skills}
                  onChange={(e) => setProfile((p) => ({ ...p, skills: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={locked}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={profile.notes}
                  onChange={(e) => setProfile((p) => ({ ...p, notes: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={locked}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <ListSection
          title="Experiences"
          rows={experiences}
          setRows={setExperiences}
          disabled={locked}
          emptyRow={emptyExperience}
          fields={[
            { key: "company", label: "Company" },
            { key: "position", label: "Position" },
            { key: "location", label: "Location" },
            { key: "start_date", label: "Start Date", type: "date" },
            { key: "end_date", label: "End Date", type: "date" },
            { key: "description", label: "Description", multiline: true, rows: 3, md: 12 },
          ]}
          updateArrayRow={updateArrayRow}
          removeArrayRow={removeArrayRow}
        />

        <ListSection
          title="Education"
          rows={educations}
          setRows={setEducations}
          disabled={locked}
          emptyRow={emptyEducation}
          fields={[
            { key: "institution", label: "Institution" },
            { key: "degree", label: "Degree" },
            { key: "field", label: "Field" },
            { key: "graduation_date", label: "Graduation Date", type: "date" },
          ]}
          updateArrayRow={updateArrayRow}
          removeArrayRow={removeArrayRow}
        />

        <ListSection
          title="Language Skills"
          rows={languages}
          setRows={setLanguages}
          disabled={locked}
          emptyRow={emptyLanguage}
          fields={[
            { key: "language", label: "Language" },
            { key: "proficiency", label: "Proficiency" },
          ]}
          updateArrayRow={updateArrayRow}
          removeArrayRow={removeArrayRow}
        />

        <ListSection
          title="Certificates"
          rows={certificates}
          setRows={setCertificates}
          disabled={locked}
          emptyRow={emptyCertificate}
          fields={[
            { key: "title", label: "Title" },
            { key: "issuer", label: "Issuer" },
            { key: "issue_date", label: "Issue Date", type: "date" },
            { key: "expiry_date", label: "Expiry Date", type: "date" },
            { key: "file_path", label: "File Path / URL", md: 12 },
          ]}
          updateArrayRow={updateArrayRow}
          removeArrayRow={removeArrayRow}
        />

        <Card>
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={generatePdf}
                disabled={generating}
              >
                Generate PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveAll}
                disabled={locked || saving}
              >
                Save
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={submitAll}
                disabled={locked || submitting}
              >
                Submit
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}

function ListSection({
  title,
  rows,
  setRows,
  disabled,
  emptyRow,
  fields,
  updateArrayRow,
  removeArrayRow,
}) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            {title}
          </Typography>
          {!disabled ? (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setRows((prev) => [...prev, emptyRow()])}
            >
              Add
            </Button>
          ) : null}
        </Stack>

        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No records yet.
          </Typography>
        ) : (
          rows.map((row, idx) => (
            <Box key={`${title}-${idx}`} sx={{ mb: 2.5 }}>
              <Grid container spacing={2}>
                {fields.map((f) => (
                  <Grid item xs={12} md={f.md || 6} key={f.key}>
                    <TextField
                      label={f.label}
                      value={row?.[f.key] ?? ""}
                      onChange={(e) => updateArrayRow(setRows, idx, f.key, e.target.value)}
                      type={f.type || "text"}
                      fullWidth
                      multiline={!!f.multiline}
                      rows={f.rows || 1}
                      disabled={disabled}
                      InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
                    />
                  </Grid>
                ))}
                {!disabled ? (
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="flex-end">
                      <IconButton color="error" onClick={() => removeArrayRow(setRows, idx)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  </Grid>
                ) : null}
              </Grid>
              {idx !== rows.length - 1 ? <Divider sx={{ mt: 2.5 }} /> : null}
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}
